import React from 'react'
import { mount } from 'enzyme'

import Probabilities from '../Probabilities'
import Probability from '../Probability'

import loan from './testData/loan'
import { darkGrey, lightGrey, darkRed, lightRed, darkGreen, lightGreen, darkOrange, lightOrange } from '../configs/probabilityColors'

describe('<Probabilities />', () => {
  let wrapper, props, instance
  beforeEach(() => {
    props = {
      cashflowRiskRating: 0.35,
      defaultRiskRating: 0.444,
      needsRedecision: loan.needsRedecision,
      product: loan.product,
      smartRate: loan.smartRate,
      productUsesDefaultModel: false
    }
    wrapper = mount(<Probabilities {...props} />)
    instance = wrapper.instance()
  })
  describe('#getDefaultColors', () => {
    describe('when the lender does not use the default model', () => {
      it('should return dark grey and light grey', () => {
        const expected = [darkGrey, lightGrey]
        const input = {
          percentToThreshold: NaN,
          needsRedecision: false,
          productUsesDefaultModel: false
        }
        expect(instance.getDefaultColors(input)).toEqual(expected)
      })
    })
    describe('when the loan needs to be redecisioned', () => {
      it('should return dark grey and light grey', () => {
        const expected = [darkGrey, lightGrey]
        const input = {
          percentToThreshold: 0.99,
          needsRedecision: true,
          productUsesDefaultModel: false
        }
        expect(instance.getDefaultColors(input)).toEqual(expected)
      })
    })
    describe('when percentToThreshold is <= .79', () => {
      it('should return dark green and light green', () => {
        const expected = [darkGreen, lightGreen]
        const input = {
          percentToThreshold: .78,
          needsRedecision: false,
          productUsesDefaultModel: true
        }
        expect(instance.getDefaultColors(input)).toEqual(expected)
      })
    })
    describe('when percentToThreshold is >= .8 and <= 0.99', () => {
      it('should return dark green light green', () => {
        const expected = [darkGreen, lightGreen]
        const input = {
          percentToThreshold: .85,
          needsRedecision: false,
          productUsesDefaultModel: true
        }
        expect(instance.getDefaultColors(input)).toEqual(expected)
      })
    })
    describe('when percentToThreshold is >=1 and <= 1.2', () => {
      it('should return dark orange and light orange', () => {
        const expected = [darkOrange, lightOrange]
        const input = {
          percentToThreshold: 1.1,
          needsRedecision: false,
          productUsesDefaultModel: true
        }
        expect(instance.getDefaultColors(input)).toEqual(expected)
      })
    })
    describe('when percentToThreshold is > 1.2', () => {
      it('should return dark red and light red', () => {
        const expected = [darkRed, lightRed]
        const input = {
          percentToThreshold: 1.3,
          needsRedecision: false,
          productUsesDefaultModel: true
        }
        expect(instance.getDefaultColors(input)).toEqual(expected)
      })
    })
    describe('default case', () => {
      it('should return dark grey and light grey', () => {
        const expected = [darkGrey, lightGrey]
        const input = {
          percentToThreshold: NaN,
          needsRedecision: false,
          productUsesDefaultModel: true
        }
        expect(instance.getDefaultColors(input)).toEqual(expected)
      })
    })
  })
  describe('#getCashflowColors', () => {
    describe('when cashflowPercent is falsy', () => {
      it('should return dark grey and light grey', () => {
        const cashflowPercent = null
        const expected = [darkGrey, lightGrey]
        expect(instance.getCashflowColors(cashflowPercent)).toEqual(expected)
      })
    })
    describe('when smartRate is > 3', () => {
      it('should return dark green and light green', () => {
        const smartRate = 4
        const expected = [darkGreen, lightGreen]
        expect(instance.getCashflowColors(smartRate)).toEqual(expected)
      })
    })
    describe('when smartRate is 3', () => {
      it('should return dark orange and light orange', () => {
        const smartRate = 3
        const expected = [darkOrange, lightOrange]
        expect(instance.getCashflowColors(smartRate)).toEqual(expected)
      })
    })
    describe('when smartRate is < 3', () => {
      it('should return dark red and light red', () => {
        const smartRate = 2
        const expected = [darkRed, lightRed]
        expect(instance.getCashflowColors(smartRate)).toEqual(expected)
      })
    })
  })
  describe('#getDefaultMessage', () => {
    describe('when the lender is not enabled for the default model', () => {
      it('should return `An R-factor is not available for the selected product.`', () => {
        const input = {
          productUsesDefaultModel: false,
          percentToThreshold: 1,
          needsRedecision: false,
          defaultThreshold: '.1'
        }
        const expected = 'An R-factor is not available for the selected product.'
        expect(instance.getDefaultMessage(input)).toBe(expected)
      })
    })
    describe('when needsRedecision is true', () => {
      it('should return `Please redecision the loan to update the R-factor.`', () => {
        const input = {
          productUsesDefaultModel: true,
          needsRedecision: true,
          percentToThreshold: .4,
          defaultThreshold: '.1'
        }
        const expected = 'Please redecision the loan to update the R-factor.'
        expect(instance.getDefaultMessage(input)).toBe(expected)
      })
    })
    describe('when percentToThreshold is falsy', () => {

    })
    describe('when percentToThreshold is <= .79', () => {
      it('should return `Great! This loan has a low R-factor.`', () => {
        const input = {
          productUsesDefaultModel: true,
          needsRedecision: false,
          percentToThreshold: .6,
          defaultThreshold: '.1'
        }
        const expected = 'Great! This loan has a low R-factor.'
        expect(instance.getDefaultMessage(input)).toBe(expected)
      })
    })
    describe('when percentToThreshold >= .8 and <= 1', () => {
      it('should return the correct message with the default threshold percentage', () => {
        const input = {
          productUsesDefaultModel: true,
          needsRedecision: false,
          percentToThreshold: .9,
          defaultThreshold: '.1'
        }
        const defaultThresholdText = `${parseInt(input.defaultThreshold * 100, 10)}%`
        const expected = `Nearing the ${defaultThresholdText} threshold. Review key attributes below.`
        expect(instance.getDefaultMessage(input)).toBe(expected)
      })
    })
    describe('when percentToThreshold >= 1', () => {
      it('should return the correct text with the default threshold percentage', () => {
        const input = {
          productUsesDefaultModel: true,
          needsRedecision: false,
          percentToThreshold: 2,
          defaultThreshold: '.1'
        }
        const defaultThresholdText = `${parseInt(input.defaultThreshold * 100, 10)}%`
        const expected = `Exceeds ${defaultThresholdText} threshold. Review key attributes below.`
        expect(instance.getDefaultMessage(input)).toBe(expected)
      })
    })
  })
  describe('#getDefaultProbability', () => {
    describe('when the lender is not enabled for the default model', () => {
      it('should return an empnty string', () => {
        const input = {
          riskRating: .1,
          needsRedecision: false,
          productUsesDefaultModel: false
        }
        const expected = ''
        expect(instance.getDefaultProbability(input)).toBe(expected)
      })
    })
    describe('when the loan needs to be redecisioned', () => {
      it('should return a question mark', () => {
        const input = {
          riskRating: .1,
          needsRedecision: true,
          productUsesDefaultModel: true
        }
        const expected = '?'
        expect(instance.getDefaultProbability(input)).toBe(expected)
      })
    })
    describe('when the riskRating is falsy', () => {
      it('should return a question mark', () => {
        const input = {
          riskRating: null,
          needsRedecision: false,
          productUsesDefaultModel: true
        }
        const expected = '?'
        expect(instance.getDefaultProbability(input)).toBe(expected)
      })
    })
    describe('when riskRating is falsy and the lender is not enabled for the default model', () => {
      it('should return an empty string', () => {
        const input = {
          riskRating: null,
          needsRedecision: false,
          productUsesDefaultModel: false
        }
        const expected = ''
        expect(instance.getDefaultProbability(input)).toBe(expected)
      })
    })
    describe('when no other cases are matched', () => {
      it('should return a percentage based on the riskRating, truncated to one decimal place', () => {
        const input = {
          riskRating: 0.333,
          needsRedecision: false,
          productUsesDefaultModel: true
        }
        const expected = '33.3%'
        expect(instance.getDefaultProbability(input)).toBe(expected)
      })
    })
  })
  describe('#getCashflowMessage', () => {
    describe('when smartRate is falsy', () => {
      it('should return `We do not have the required information to predict this.`', () => {
        const smartRate = null
        const expected = 'We do not have the required information to predict this.'
        expect(instance.getCashflowMessage(smartRate)).toBe(expected)
      })
    })
    describe('when smartRate is falsy', () => {
      it('should return text with the smart rate', () => {
        const smartRate = 5
        const expected = `SmartRate ${smartRate}`
        expect(instance.getCashflowMessage(smartRate)).toBe(expected)
      })
    })
  })
  describe('#getCashflowPercent', () => {
    describe('when riskRating is falsy', () => {
      it('should return an empty string', () => {
        const riskRating = undefined
        const expected = ''
        expect(instance.getCashflowPercent(riskRating)).toBe(expected)
      })
    })
    describe('when riskRating is a number', () => {
      it('should return', () => {
        const riskRating = 0.444
        const expected = '44.4%'
        expect(instance.getCashflowPercent(riskRating)).toBe(expected)
      })
    })
  })
  describe('#render', () => {
    it('should render two <Probability />s', () => {
      expect(wrapper.find(Probability)).toHaveLength(2)
    })
  })
})
